// Sim engagement funnel card. Radial render of the event-level status
// split (didn't engage / clicked 1st / clicked 2nd / reported) for the
// active period. Event-level so every percentage matches the headline
// KPI rates exactly: prefers the same uncapped server aggregate the KPI
// cards read (agg prop), then the scoped event log, then the precomputed
// PERIOD_DATA fallback.
//
// Postgres mapping: `sim_period_metrics.funnel_json` derived from
// GROUP BY status counts on `simulations_sent` for the period.
// Integration hook: covered by useSimsAnalyticsQuery.
import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../../../components/cards/MainCard';
import ReactApexChart from 'react-apexcharts';
import { motion, AnimatePresence } from 'framer-motion';
import { PERIOD_DATA } from '../../data';

const CHART_HEIGHT = 240;

const SEGMENT_META = [
    { label: "Didn't engage", color: '#A0A8B8' },
    { label: 'Clicked (1st)', color: '#FFCA28' },
    { label: 'Clicked (2nd)', color: '#FF5252' },
    { label: 'Reported',      color: '#66BB6A' },
];

function BarRow({ label, n, pct, color, delay, isDark, maxPct }) {
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const t = setTimeout(() => setWidth(maxPct > 0 ? (pct / maxPct) * 100 : pct), 120 + delay * 1000);
        return () => clearTimeout(t);
    }, [pct, delay, maxPct]);

    return (
        <Box sx={{ cursor: 'default', '&:hover .bar-label': { color: 'text.primary' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color, flexShrink: 0 }} />
                    <Typography className="bar-label" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', transition: 'color 150ms' }}>{label}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{n.toLocaleString()}</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', width: 30, textAlign: 'right' }}>{pct}%</Typography>
                </Box>
            </Box>
            <LinearProgress
                variant="determinate"
                value={width}
                sx={{
                    height: 20, borderRadius: 1,
                    bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'grey.100',
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 1,
                        bgcolor: color,
                        transition: 'transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                        transitionDelay: `${delay}s`,
                        '&::after': {
                            content: '""', position: 'absolute', top: 0, left: '-100%',
                            width: '100%', height: '100%',
                            background: `linear-gradient(90deg, transparent, ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.4)'}, transparent)`,
                            animation: width > 0 ? 'funnel-shine 2s ease-in-out' : 'none',
                            animationDelay: `${delay + 0.5}s`, borderRadius: 1,
                        },
                    },
                    overflow: 'hidden',
                    '@keyframes funnel-shine': {
                        '0%': { left: '-100%' }, '100%': { left: '100%' },
                    },
                }}
            />
        </Box>
    );
}

// Active [startMs, endMs] window (see HeatmapCard). Custom range uses explicit
// dates; falls back to preset/90d when none supplied (e.g. summary page).
function periodWindow(period, startDate, endDate) {
    const days = { Today: 1, '7d': 7, '30d': 30, '90d': 90, '6m': 182, '12m': 365 }[period];
    const now = Date.now();
    if (days != null) return [now - days * 86400000, now];
    const s = startDate ? Date.parse(startDate + 'T00:00:00Z') : (now - 90 * 86400000);
    const e = endDate ? Date.parse(endDate + 'T23:59:59Z') : now;
    return [s, e];
}

export default function FunnelCard({ period, log, agg, startDate, endDate }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const pd = PERIOD_DATA[period] || PERIOD_DATA['7d'];
    const { TOTAL_SENT, SEGMENTS } = React.useMemo(() => {
        // Server aggregate first: the same uncapped counts the KPI cards read,
        // so the funnel and headline rates cannot disagree.
        if (agg && agg.sent > 0) {
            const c2 = Math.max(0, agg.landings || 0);
            const c1 = Math.max(0, (agg.clicks || 0) - c2);
            const rep = Math.max(0, agg.reports || 0);
            const noEngage = Math.max(0, agg.sent - c1 - c2 - rep);
            const segCounts = [noEngage, c1, c2, rep];
            return { TOTAL_SENT: agg.sent, SEGMENTS: SEGMENT_META.map((m, i) => ({ ...m, n: segCounts[i] })) };
        }
        if (!log) return { TOTAL_SENT: pd.funnelSent, SEGMENTS: SEGMENT_META.map((m, i) => ({ ...m, n: pd.funnelSegments[i] })) };
        // Event-level partition (each send carries exactly one status), matching
        // the scoped KPI computation over the same log.
        const [winStart, winEnd] = periodWindow(period, startDate, endDate);
        let total = 0, c1 = 0, c2 = 0, rep = 0;
        for (const e of log) {
            const t = Date.parse(e.dateSent + 'T00:00:00Z');
            if (Number.isNaN(t) || t < winStart || t > winEnd) continue;
            total += 1;
            if (e.status === 'clicked' || e.status === 'attachment') c1 += 1;
            else if (e.status === 'clicked_2nd') c2 += 1;
            else if (e.status === 'reported') rep += 1;
        }
        const segCounts = [Math.max(0, total - c1 - c2 - rep), c1, c2, rep];
        return { TOTAL_SENT: Math.max(1, total), SEGMENTS: SEGMENT_META.map((m, i) => ({ ...m, n: segCounts[i] })) };
    }, [agg, log, period, pd, startDate, endDate]);
    const [centre, setCentre] = useState({ value: TOTAL_SENT, label: 'Sent' });
    const [showBars, setShowBars] = useState(() => localStorage.getItem('rpt_funnel_view') === 'bars');

    useEffect(() => { setCentre({ value: TOTAL_SENT, label: 'Sent' }); }, [TOTAL_SENT]);

    const pcts = SEGMENTS.map((s) => Math.round((s.n / TOTAL_SENT) * 100));

    const options = {
        chart: {
            type: 'radialBar',
            background: 'transparent',
            animations: { enabled: true, speed: 900, animateGradually: { enabled: true, delay: 150 } },
            events: {
                dataPointMouseEnter: (e, ctx, config) => {
                    const seg = SEGMENTS[config.dataPointIndex];
                    if (seg) setCentre({ value: seg.n, label: seg.centreLabel || seg.label });
                },
                dataPointMouseLeave: () => {
                    setCentre({ value: TOTAL_SENT, label: 'Sent' });
                },
            },
        },
        colors: SEGMENTS.map((s) => s.color),
        labels: SEGMENTS.map((s) => s.label),
        plotOptions: {
            radialBar: {
                offsetY: 0,
                startAngle: -135,
                endAngle: 135,
                hollow: { size: '32%', background: 'transparent' },
                track: { background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)', strokeWidth: '100%', margin: 4 },
                dataLabels: { show: false },
            },
        },
        stroke: { lineCap: 'round' },
        legend: { show: false },
        tooltip: {
            theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
            style: { fontSize: '11px', fontFamily: 'Inter' },
            y: { formatter: (v) => `${v}%` },
        },
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            style={{ height: '100%' }}
        >
            <MainCard content={false} sx={{ bgcolor: 'background.paper', overflow: 'visible' }} border={false} boxShadow={true}>
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                                Simulation engagement
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                Broken down by user count
                            </Typography>
                        </Box>
                        <Box
                            component="button"
                            onClick={() => setShowBars(b => { const next = !b; localStorage.setItem('rpt_funnel_view', next ? 'bars' : 'radial'); return next; })}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                px: '8px', py: '3px',
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)'}`,
                                borderRadius: '5px',
                                background: isDark ? '#333537' : '#ffffff',
                                cursor: 'pointer',
                                color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
                                fontSize: 10,
                                fontFamily: 'Inter, sans-serif',
                                flexShrink: 0,
                                transition: 'border-color 150ms, color 150ms',
                                '&:hover': { borderColor: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)' },
                            }}
                        >
                            {showBars ? (
                                <>
                                    <Box component="span" sx={{ width: 16, height: 10, position: 'relative', display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'space-around' }}>
                                        {[1, 0.6].map((op, i) => (
                                            <Box key={i} sx={{ width: '100%', height: '2px', bgcolor: 'currentColor', borderRadius: '1px', opacity: op }} />
                                        ))}
                                    </Box>
                                    Radial
                                </>
                            ) : (
                                <>
                                    <Box component="span" sx={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: 10 }}>
                                        {[4, 7, 5, 9, 6].map((h, i) => (
                                            <Box key={i} sx={{ width: 2, height: h, bgcolor: 'currentColor', borderRadius: '1px' }} />
                                        ))}
                                    </Box>
                                    Bars
                                </>
                            )}
                        </Box>
                    </Box>

                    <AnimatePresence mode="wait" initial={false}>
                        {showBars ? (
                            <motion.div key="bars" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
                                    {SEGMENTS.map((s, i) => {
                                        const pct = pcts[i];
                                        return (
                                            <BarRow key={s.label} label={s.label} n={s.n} pct={pct} color={s.color} delay={i * 0.08} isDark={isDark} maxPct={pcts[0]} />
                                        );
                                    })}
                                </Box>
                            </motion.div>
                        ) : (
                            <motion.div key="radial" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
                                {/* Chart, fixed height, overflow visible so ApexCharts tooltip isn't clipped */}
                                <Box
                                    sx={{ position: 'relative', height: CHART_HEIGHT, flexShrink: 0, overflow: 'visible' }}
                                    onMouseLeave={() => setCentre({ value: TOTAL_SENT, label: 'Sent' })}
                                >
                                    <ReactApexChart
                                        options={options}
                                        series={pcts}
                                        type="radialBar"
                                        height={CHART_HEIGHT}
                                        width="100%"
                                    />
                                    <Box sx={{
                                        position: 'absolute', top: '49%', left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        textAlign: 'center', pointerEvents: 'none', width: 90,
                                    }}>
                                        <AnimatePresence mode="wait">
                                            <motion.div
                                                key={centre.value}
                                                initial={{ opacity: 0, y: 4 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -4 }}
                                                transition={{ duration: 0.15 }}
                                            >
                                                <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>
                                                    {centre.value}
                                                </Typography>
                                                <Typography sx={{ fontSize: 9, fontWeight: 600, color: 'text.secondary', mt: '2px', lineHeight: 1.2, whiteSpace: 'pre-line' }}>
                                                    {centre.label}
                                                </Typography>
                                            </motion.div>
                                        </AnimatePresence>
                                    </Box>
                                </Box>

                                {/* Legend */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.75, mt: 1, px: 1 }}>
                                    {SEGMENTS.map((s) => (
                                        <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flex: 1, minWidth: 0 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: s.color, flexShrink: 0, mt: '1px' }} />
                                                <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary', lineHeight: 1.3 }}>
                                                    {s.label}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.primary', flexShrink: 0 }}>
                                                {s.n}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Box>
            </MainCard>
        </motion.div>
    );
}
